import { observable, action, computed } from 'mobx'
import { extractMessageFromError } from '@/utils/error-util'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { validationContext, required, pattern, func } from 'validx'
import { task } from 'mobx-task'
import { type WorkspaceMembershipStore } from '@/features/iam/workspaces/WorkspaceMembershipStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { type MemberStore } from '@/features/iam/members/MemberStore'
import { type SessionStore } from '@/features/iam/auth/SessionStore'
import { type WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { ModalViewModel } from '@/infra/ModalViewModel'

export type ConfigurerView = 'Search' | 'Register'

export interface ProfileInfo {
  givenName: string
  familyName: string
  phone: string
  email: string
}

export class WorkspaceSignupViewModel {
  @observable accessor configurerView: ConfigurerView = 'Search'
  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor slug = ''
  @observable accessor lookUpWorkspace: Workspace | null = null
  @observable accessor workspace: Workspace | null = null

  /**
   * The workspace configurer, which replaces the workspace selector.
   */
  readonly configurer = new ModalViewModel()

  readonly validation = validationContext<WorkspaceSignupViewModel>(this, {
    givenName: [
      required(),
      func(({ value }: { value: string }) => {
        return (
          value.length <= 40 || "Given name can't be more than 40 characters"
        )
      }),
      func(({ value }: { value: string }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
    familyName: [
      required(),
      func(({ value }: { value: string }) => {
        return (
          value.length <= 40 || "Family name can't be more than 40 characters"
        )
      }),
      func(({ value }: { value: string }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
    email: [
      required(),
      pattern('email'),
      func(({ value }: { value: string }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
    ],
    phone: [
      required(),
      func(({ value }: { value: string }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
      func(({ value }: { value: string }) => {
        return value.trim() !== '' || 'This field is required'
      }),
    ],
  })

  constructor(
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _sessionStore: SessionStore,
    private readonly _memberStore: MemberStore,
    private readonly _workspaceMembershipsStore: WorkspaceMembershipStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _checkMemberExists: (
      workspace: Workspace,
    ) => Promise<boolean>,
  ) {}

  /**
   * Prefills the form from the user and joins the selected workspace when the
   * user's details are already complete.
   */
  async prefillAndSync() {
    this._prefillFromUser()

    if (this.validation.reset().validate().isValid) {
      await this._syncMember()
    } else {
      // reset it so that we dont show error messages right away
      this.validation.reset()
    }
  }

  @computed
  get memberships() {
    return this._workspaceMembershipsStore.memberships
  }

  @action.bound
  setConfigurerView(option: ConfigurerView) {
    this.configurerView = option
  }

  @action.bound
  openWorkspaceSearch() {
    this.setConfigurerView('Search')
    this.configurer.show()
  }

  @action.bound
  closeWorkspaceSearch() {
    this.slug = ''
    if (this.searchWorkspace.rejected) {
      this.validation.reset()
      this.searchWorkspace.reset()
    }
    this.configurer.close()
  }

  @computed
  get canSearchWorkspace() {
    return this.slug.trim().length !== 0
  }

  /**
   * Why the workspace search failed, if it did.
   */
  @computed
  get slugError() {
    return this.searchWorkspace.rejected
      ? extractMessageFromError(this.searchWorkspace.error)
      : undefined
  }

  @action.bound
  setGivenName(value: string) {
    this.givenName = value
  }

  @action.bound
  setFamilyName(value: string) {
    this.familyName = value
  }

  @action.bound
  setEmail(value: string) {
    this.email = value
  }

  @action.bound
  setPhone(value: string) {
    this.phone = value
  }

  @action.bound
  setSlug(value: string) {
    this.slug = value
  }

  /**
   * Searches for the workspace with the entered slug.
   */
  @action.bound
  async search() {
    if (this.canSearchWorkspace) {
      // The slug input shows the failure.
      await this.searchWorkspace().catch(() => undefined)
    }
  }

  /**
   * Joins the selected workspace with the entered details.
   */
  @action.bound
  async submit() {
    await this._syncMember()
  }

  searchWorkspace = task.resolved(async () => {
    return this._workspaceStore.fetchWorkspaceByIdOrSlug(this.slug).then(
      action((found) => {
        this.lookUpWorkspace = found
        if (found && this.memberships.some((m) => m.workspace === found)) {
          throw new Error('You are already a member of this workspace.')
        }

        // The search only looks the workspace up; joining runs on its own
        // and fails silently.
        void this._joinWorkspace().catch(() => undefined)
      }),
    )
  })

  private async _joinWorkspace() {
    if (!this.lookUpWorkspace) {
      return
    }
    const workspace = await this._workspaceStore.fetchWorkspace(
      this.lookUpWorkspace.id,
    )

    if (!workspace) {
      throw new Error('The workspace you selected does not exist.')
    }
    this.setWorkspace(workspace)
    if (!(await this._checkMemberExists(workspace))) {
      return
    }
    await this._sessionStore.selectWorkspace(workspace)
  }

  /**
   * @param workspace The workspace to sign up for.
   */
  async maybeSyncMember(workspace: Workspace) {
    this.setWorkspace(workspace)
    // Try to create the member if we already have their info. Only missing
    // info shows the registration form; a failed save leaves it closed.
    const synced = await this._syncMember()
    if (synced !== false) {
      return
    }
    // ... If not, show the registration form.
    this.configurer.show()
    this.setConfigurerView('Register')
  }

  @action.bound
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  trySyncMember = task.resolved(async (): Promise<boolean> => {
    if (!this.workspace) {
      // This member is already associated to a workspace.
      return false
    }

    if (this.validation.reset().validate().isValid === false) {
      return false
    }

    const profileInfo: ProfileInfo = {
      givenName: this.givenName.trim(),
      familyName: this.familyName.trim(),
      email: this.email.trim(),
      phone: this.phone.trim(),
    }
    const member = await this._memberStore.fetchMember({
      workspaceId: this.workspace.id,
      userId: this._sessionStore.currentUser.id,
      userPublicId: this._sessionStore.currentUser.publicId,
    })
    await (
      member?.internalId
        ? this._updateMember(member, profileInfo)
        : this._createMember(this.workspace, profileInfo)
    ).catch(this._createAlertErrorHandler('Saving profile failed'))

    this.configurer.close()
    await this._sessionStore.selectWorkspace(this.workspace)
    return true
  })

  /**
   * Resolves `null` when the sync failed; `trySyncMember` has already alerted
   * a failed member save.
   */
  private _syncMember(): Promise<boolean | null> {
    return this.trySyncMember().catch(() => null)
  }

  @action.bound
  private _prefillFromUser() {
    const user = this._sessionStore.currentUser

    this.email = user.email || ''
    this.givenName = user.givenName || ''
    this.familyName = user.familyName || ''
    this.phone = user.phone || ''
  }

  private async _updateMember(member: Member, profileInfo: ProfileInfo) {
    await this._memberStore.updateProfile(member, profileInfo)
  }

  private async _createMember(workspace: Workspace, profileInfo: ProfileInfo) {
    const member = await this._memberStore.createWithProfile({
      workspaceId: workspace.id,
      userId: this._sessionStore.currentUser.id,
      ...profileInfo,
    })

    // Member-only workspace info is only returned once the member exists.
    await this._workspaceStore.fetchWorkspace(workspace.id, { force: true })
    this._workspaceMembershipsStore.addMembership({
      workspace,
      member,
    })
  }
}
